import React, { useEffect, useState } from 'react'
import {ChevronLeft, ChevronRight} from 'lucide-react'
import { lumi } from '../../../lib/lumi'
import type { Lang } from './types'

interface Partner {
  _id: string
  companyName: string
  companyNameEn?: string
  slug?: string
  logo?: string
  images?: string[]
  story?: string
  storyEn?: string
  isPublic?: boolean
  status: string
  signatureDrinks?: Array<{ name: string; description?: string }>
  signatureDrinksEn?: Array<{ name: string; description?: string }>
}

interface Props {
  currentLang: Lang
}

/**
 * Minimal Barista Map style partners carousel — square image on the left,
 * minimal partner info on the right, single ← → control row below.
 */
const WholesalePartnersCarouselV2: React.FC<Props> = ({ currentLang }) => {
  const [partners, setPartners] = useState<Partner[]>([])
  const [idx, setIdx] = useState(0)

  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const result = await lumi.entities.partners.list({
          filter: { isPublic: true, status: 'active' },
          limit: 20,
        })
        setPartners(result?.list || [])
      } catch (error) {
        console.error('Failed to fetch partners:', error)
      }
    }
    fetchPartners()
  }, [])

  if (partners.length === 0) return null

  const p = partners[idx]
  const name =
    currentLang === 'en' && p.companyNameEn ? p.companyNameEn : p.companyName
  const story =
    currentLang === 'en' && p.storyEn ? p.storyEn : p.story
  const drinks =
    currentLang === 'en' && p.signatureDrinksEn?.length
      ? p.signatureDrinksEn
      : p.signatureDrinks

  return (
    <section className="bg-[#F7F4EE] py-16 md:py-24">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10">
        <p
          className="mb-3 text-[12px] tracking-[0.22em] text-black/65"
          style={{
            fontFamily: "'EB Garamond', serif",
            fontStyle: 'italic',
            fontWeight: 400,
          }}
        >
          {currentLang === 'zh' ? '合作店家與品牌夥伴' : 'Partner Stores'}
        </p>
        <h2
          className="mb-12 text-black"
          style={{
            fontFamily: "'EB Garamond', 'Noto Serif TC', serif",
            fontWeight: 400,
            fontSize: 'clamp(1.75rem, 3vw, 2.5rem)',
            letterSpacing: '0.03em',
            lineHeight: 1.25,
          }}
        >
          {currentLang === 'zh'
            ? '為他們的招牌而存在的咖啡豆'
            : 'Coffee Beans Made for Their Signature'}
        </h2>

        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 md:gap-16">
          {/* Image */}
          <div className="aspect-square w-full overflow-hidden bg-black/5">
            {p.images?.[0] || p.logo ? (
              <img
                src={p.images?.[0] || p.logo}
                alt={name}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <span
                  className="text-2xl text-black/40"
                  style={{
                    fontFamily: "'EB Garamond', serif",
                    fontWeight: 300,
                  }}
                >
                  {name}
                </span>
              </div>
            )}
          </div>

          {/* Text */}
          <div className="flex flex-col justify-center">
            <h3
              className="mb-6 text-black"
              style={{
                fontFamily: "'EB Garamond', 'Noto Serif TC', serif",
                fontWeight: 400,
                fontSize: 'clamp(1.5rem, 2.4vw, 2rem)',
                letterSpacing: '0.03em',
                lineHeight: 1.25,
              }}
            >
              {name}
            </h3>

            {story && (
              <p
                className="mb-8 text-[14px] leading-loose text-black/70"
                style={{
                  fontFamily: "'Noto Serif TC', 'Noto Sans TC', sans-serif",
                  fontWeight: 300,
                  letterSpacing: '0.03em',
                }}
              >
                {story}
              </p>
            )}

            {drinks && drinks.length > 0 && (
              <div className="border-t border-black/15 pt-6">
                <p
                  className="mb-3 text-[11px] tracking-[0.22em] text-black/55"
                  style={{
                    fontFamily: "'EB Garamond', serif",
                    fontStyle: 'italic',
                    fontWeight: 400,
                  }}
                >
                  {currentLang === 'zh' ? 'Signature Drinks' : 'Signature Drinks'}
                </p>
                <ul
                  className="space-y-1.5 text-[13px] text-black/75"
                  style={{
                    fontFamily: "'Noto Serif TC', 'Noto Sans TC', sans-serif",
                    fontWeight: 300,
                    letterSpacing: '0.03em',
                  }}
                >
                  {drinks.map((d, i) => (
                    <li key={i}>
                      — {d.name}
                      {d.description && (
                        <span className="text-black/45"> ({d.description})</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Pager */}
        <div className="mt-12 flex items-center justify-between border-t border-black/15 pt-8">
          <span
            className="text-[12px] tracking-[0.22em] text-black/55"
            style={{
              fontFamily: "'EB Garamond', serif",
              fontStyle: 'italic',
              fontWeight: 400,
            }}
          >
            {String(idx + 1).padStart(2, '0')} /{' '}
            {String(partners.length).padStart(2, '0')}
          </span>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() =>
                setIdx((prev) =>
                  prev === 0 ? partners.length - 1 : prev - 1
                )
              }
              className="flex h-11 w-11 items-center justify-center border border-black/30 transition-colors hover:bg-black hover:text-white"
              aria-label="Previous partner"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() =>
                setIdx((prev) =>
                  prev === partners.length - 1 ? 0 : prev + 1
                )
              }
              className="flex h-11 w-11 items-center justify-center border border-black/30 transition-colors hover:bg-black hover:text-white"
              aria-label="Next partner"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}

export default WholesalePartnersCarouselV2
